import './index.scss';

import React, { useEffect, forwardRef, useImperativeHandle, Fragment } from 'react';
import { IPdfReaderProps, IPdfReaderRef } from './types';
import { usePdfReader } from './hooks/usePdfReader';
import { PdfToolbar } from './ui/PdfToolbar';
import { PdfSidebar } from './ui/PdfSidebar';
import { PdfViewport } from './ui/PdfViewport';
import { TranscodingStatus } from './components/transcodingStatus';
import { useScrollingHideToolbar } from './hooks/useScrollingHideToolbar';
import { lang } from '@umu/ukit-lang';
import classNames from 'classnames';
import { isMobile } from '@umu/umu-ua';
import { onDocumentContextMenu } from './utils/common';
import { childPostControl } from '@umu/umu-iframe/src/session/postMessage/childPostControl';
import { WaterMarkWrap } from './components/waterMarkWrap';

export const PdfReader = forwardRef<IPdfReaderRef, IPdfReaderProps>((props, ref) => {
    const {
        resourceUrl,
        transStatus,
        className = '',
        style = {},
        waterMarkOptions,
        userInfo,
        documentTitle,
        isShowWatermark: allowShowWatermark = false,
        loadingComponent,
        onFailedCallback,
    } = props;
    const { state, controller, refs, toolbarProps, isMobileShowSearchDrawer, isManualScrolling } = usePdfReader(props);
    const { loading, error, pageNum, totalPages, screenState, showOutlines, outlines, pdfDoc, isShowBar } = state;
    const isRendered = !loading && !!pdfDoc;
    const isShowWatermark = allowShowWatermark && isRendered;
    const shouldMountSidebar = !isMobile && outlines.length > 0;
    const isSidebarVisible = shouldMountSidebar && screenState === 'full' && showOutlines;

    // 滚动隐藏工具栏功能（全屏模式下）
    useScrollingHideToolbar({
        containerRef: refs.containerRef,
        screenState,
        isShowBar,
        isSearchDrawerOpen: isMobileShowSearchDrawer,
        isManualScrolling,
        onShowBar: controller.showToolbar,
        onHideBar: controller.hideToolbar,
    });

    // 通过 ref 暴露工具栏相关方法
    useImperativeHandle(ref, () => controller as IPdfReaderRef, [controller]);

    // Handle callbacks
    useEffect(() => {
        if (error || !resourceUrl) {
            onFailedCallback?.();
        }
    }, [error, resourceUrl]);

    useEffect(() => {
        childPostControl.sendSideBarFold(!isSidebarVisible, true);
    }, [isSidebarVisible]);

    if (error || !resourceUrl) {
        return (
            <div className="doc-container">
                <p className="empty-tips">{error ? lang('该页面加载失败') : lang('该内容已删除')}</p>
            </div>
        );
    }

    if (loading) {
        return <Fragment>{loadingComponent}</Fragment>;
    }
    return (
        <div className={`doc-reader ${screenState} ${className}`} style={style}>
            {shouldMountSidebar && (
                <PdfSidebar
                    pdfDoc={pdfDoc}
                    totalPages={totalPages}
                    currentPage={pageNum.value}
                    outlines={outlines}
                    showOutlines={showOutlines}
                    className={screenState}
                    documentTitle={documentTitle}
                    onToggleOutline={controller.toggleOutline}
                    onCloseOutline={controller.closeOutline}
                    onPageJump={controller.jumpTo}
                    mainViewportScale={state.initScale}
                />
            )}
            <div className={`doc-container doc-content-new ${screenState}`} ref={refs.setContainerRef}>
                <div
                    className={classNames('doc-container-wrap', {
                        'doc-container-wrap-hide-search': !isMobileShowSearchDrawer,
                    })}
                    onContextMenu={onDocumentContextMenu}
                >
                    <PdfToolbar {...toolbarProps} />
                    <TranscodingStatus transStatus={transStatus} />
                    <PdfViewport state={state} controller={controller} containerRef={refs.containerRef}>
                        {isShowWatermark && (
                            <WaterMarkWrap
                                isFullscreen={screenState === 'full'}
                                waterMarkOptions={waterMarkOptions}
                                defaultText={userInfo?.user_name || ''}
                            />
                        )}
                    </PdfViewport>
                </div>
            </div>
        </div>
    );
});

PdfReader.displayName = 'PdfReader';
